import React, { Fragment } from "react";
import { ChangeEvent } from "react";
import { Prompt } from "react-router";

import { DateTime } from "luxon";

import { IAuthProps, WellKnownGroups } from "../AuthContext";
import DelayInput from "../DelayInput";

import { apiGet, V2 } from "../../Api";
import { formatName } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import TabControl from "../TabControl";
import ViewToolbar from "../ViewToolbar";
import ToolbarMenuItem from "../ToolbarMenuItem";

type ViewMode = "office" | "surgery" | "write";

interface IMetricsNpCasesViewViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    isWriteAllowed: boolean;
    hasChanged: boolean;
    searchStart: string;
    searchEnd: string;
    searchMonth: string;
    dateType: "month" | "range";

    mode: ViewMode;

    employees: any[];

    [name: string]: any;
}


class MetricsNpCasesView extends React.Component<IAuthProps, IMetricsNpCasesViewViewState> {
    constructor(props: Readonly<IAuthProps>) {
        super(props);

        const today = DateTime.local();

        this.state = {
            isLoading: true,
            isSubmitting: false,
            isWriteAllowed: true,
            hasChanged: false,
            searchStart: today.startOf("month").toISODate(),
            searchEnd: today.endOf("month").toISODate(),
            searchMonth: today.startOf("month").toFormat("yyyy-MM"),
            dateType: "month",

            mode: "office",

            employees: []
        };

        this.search = this.search.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.saveData = this.saveData.bind(this);
        this.cancelData = this.cancelData.bind(this);
        this.toggleWriteMode = this.toggleWriteMode.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
    }

    public componentDidMount() {
        this.search();
    }

    public componentDidUpdate(prevProps: Readonly<IAuthProps>, prevState: Readonly<IMetricsNpCasesViewViewState>) {
        if (this.state.searchStart !== prevState.searchStart && !this.state.searchStart) {
            // Default back to safe value
            this.setState({
                searchStart: DateTime.local().startOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchEnd !== prevState.searchEnd && !this.state.searchEnd) {
            // Default back to safe value
            this.setState({
                searchEnd: DateTime.local().endOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchMonth !== prevState.searchMonth && !this.state.searchMonth) {
            // Default back to safe value
            this.setState({
                searchMonth: DateTime.local().startOf("month").toFormat("yyyy-MM")
            }, () => this.search());
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <Prompt when={this.state.mode === "write" && this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <ToolbarMenuItem condition={this.state.mode !== "write"} right="metrics:prov:write">
                        <button className="button icon-list-ul" title="Edit daily values" onClick={this.toggleWriteMode} />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <div className="filter-control-box">
                    {
                        this.state.mode !== "write"
                            ? <Fragment>
                                <select name="dateType" value={this.state.dateType} onChange={this.handleElementChange}>
                                    <option value="month">Month</option>
                                    <option value="range">Date Range</option>
                                </select>
                                {
                                    this.state.dateType === "range"
                                        ? <Fragment>
                                            <div className="control-group fill-space">
                                                <span>Start Date:</span>
                                                <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                            </div>
                                            <div className="control-group fill-space">
                                                <span>End Date:</span>
                                                <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                                            </div>
                                        </Fragment>
                                        : <DelayInput type="month" className="fill-space" timeout={500} value={this.state.searchMonth} name="searchMonth" onChangeDelayed={this.handleElementChange} />
                                }
                            </Fragment>
                            : <Fragment>
                                <div className="control-group fill-space">
                                    <span>Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                </div>
                            </Fragment>
                    }
                </div>
                {
                    this.state.mode !== "write"
                        ? <TabControl headers={["Office Appointments", "Surgery Cases"]} onTabChanged={this.handleTabChange}>
                            <Fragment>
                                <table className="table-vertical">
                                    <thead>
                                        <tr>
                                            <th />
                                            <th colSpan={3} className="col-smallest col-center">HF</th>
                                            <th colSpan={3} className="col-smallest col-center">Non-HF</th>
                                        </tr>
                                        <tr>
                                            <th>Name</th>
                                            <th className="col-smallest col-center th-font-tiny">Annual</th>
                                            <th className="col-smallest col-center th-font-tiny">Gyn</th>
                                            <th className="col-smallest col-center col-bold th-font-tiny">Total</th>

                                            <th className="col-smallest col-center th-font-tiny">Annual</th>
                                            <th className="col-smallest col-center th-font-tiny">Gyn</th>
                                            <th className="col-smallest col-center col-bold th-font-tiny">Total</th>
                                        </tr>
                                    </thead>
                                    <thead className="table-vertical-head-small">
                                        <tr>
                                            <th>Provider case count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            this.state.isLoading
                                                ? <tr>
                                                    <td colSpan={10}>
                                                        {
                                                            typeof this.state.isLoading === "string"
                                                                ? <span className="error-text">{this.state.isLoading}</span>
                                                                : <div className="spinner-center lds-dual-ring" />
                                                        }
                                                    </td>
                                                </tr>
                                                : this.state.employees.length < 1
                                                    ? <tr>
                                                        <td colSpan={10}>No data available</td>
                                                    </tr>
                                                    : this.state.employees.map((a, i) => (
                                                        <tr key={a.id || a.employee.id} className={a.id === "total" ? "col-bold" : undefined}>
                                                            <td className="inner-edge" data-caption="Name: ">{formatName(a.name || a.employee.name)}</td>
                                                            <td className="col-center inline" data-caption="HF Annual: ">{a.hfAnnual || ""}</td>
                                                            <td className="col-center inline inner-edge" data-caption="HF Gyn: ">{a.hfGyn || ""}</td>
                                                            <td className="col-center inline inner-edge" data-caption="HF Total: ">{a.hfAnnual + a.hfGyn}</td>

                                                            <td className="col-center inline" data-caption="Non-HF Annual: ">{a.annual || ""}</td>
                                                            <td className="col-center inline inner-edge" data-caption="Non-HF Gyn: ">{a.gyn || ""}</td>
                                                            <td className="col-center inline" data-caption="Non-HF Total: ">{a.annual + a.gyn}</td>
                                                        </tr>
                                                    ))
                                        }
                                    </tbody>
                                </table>
                                <p>
                                    <em>
                                        <small>
                                            Note: This displays only completed appointments scheduled for the date range specified.
                                        </small>
                                    </em>
                                </p>
                            </Fragment>
                            <Fragment>
                                <table className="table-vertical">
                                    <thead>
                                        <tr>
                                            <th>Name</th>

                                            <th className="col-smallest col-center th-font-tiny" title="Pending & Active">Outstanding</th>
                                            <th className="col-smallest col-center th-font-tiny">Canceled</th>
                                            <th className="col-smallest col-center th-font-tiny">Done</th>
                                            <th className="col-smallest col-center col-bold th-font-tiny">Total</th>
                                        </tr>
                                    </thead>
                                    <thead className="table-vertical-head-small">
                                        <tr>
                                            <th>Provider case count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            this.state.isLoading
                                                ? <tr>
                                                    <td colSpan={15}>
                                                        {
                                                            typeof this.state.isLoading === "string"
                                                                ? <span className="error-text">{this.state.isLoading}</span>
                                                                : <div className="spinner-center lds-dual-ring" />
                                                        }
                                                    </td>
                                                </tr>
                                                : this.state.employees.length < 1
                                                    ? <tr>
                                                        <td colSpan={15}>No data available</td>
                                                    </tr>
                                                    : this.state.employees.map((a, i) => (
                                                        <tr key={a.id || a.employee.id} className={a.id === "total" ? "col-bold" : undefined}>
                                                            <td className="inner-edge" data-caption="Name: ">{formatName(a.name || a.employee.name)}</td>

                                                            <td className="col-center inline" data-caption="Pending & Active: ">{a.pending || ""}</td>
                                                            <td className="col-center inline" data-caption="Canceled: ">{a.cancel || ""}</td>
                                                            <td className="col-center inline inner-edge" data-caption="Done: ">{a.done || ""}</td>
                                                            <td className="col-center inline" data-caption="Total: ">{a.total}</td>
                                                        </tr>
                                                    ))
                                        }
                                    </tbody>
                                </table>
                                <p>
                                    <em>
                                        <small>
                                            Note: This displays surgeries scheduled for the date range specified.
                                        </small>
                                    </em>
                                </p>
                            </Fragment>
                        </TabControl>
                        : undefined
                }
                {
                    this.state.mode === "write"
                        ? <Fragment>
                            <p>
                                Enter the required data below, then press Save when finished.
                                    </p>
                            <table className="table-vertical">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th className="col-smallest">Gyn</th>
                                        <th className="col-smallest">Annual</th>
                                        <th className="col-smallest">Total</th>
                                    </tr>
                                </thead>
                                <thead className="table-vertical-head-small">
                                    <tr>
                                        <th>Provider case count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        this.state.isLoading
                                            ? <tr>
                                                <td colSpan={10}>
                                                    {
                                                        typeof this.state.isLoading === "string"
                                                            ? <span className="error-text">{this.state.isLoading}</span>
                                                            : <div className="spinner-center lds-dual-ring" />
                                                    }
                                                </td>
                                            </tr>
                                            : this.state.employees.map((a, i) => (
                                                <tr key={a.id || a.employee.id}>
                                                    <td data-caption="Name: ">{formatName(a.name || a.employee.name)}</td>
                                                    <td className="inline col-right" data-caption="Total Gyn: ">{this.state.mode !== "write" ? a.gyn : <input type="number" className="input-tiny input-right" value={a.gyn || ""} onChange={e => this.handleRowInputChange(i, "gyn", e)} />}</td>
                                                    <td className="inline col-right" data-caption="Total Annual: ">{this.state.mode !== "write" ? a.annual : <input type="number" className="input-tiny input-right" value={a.annual || ""} onChange={e => this.handleRowInputChange(i, "annual", e)} />}</td>
                                                    <td className="inline col-right" data-caption="Total: ">{+(a.gyn || 0) + +(a.annual || 0)}</td>
                                                </tr>
                                            ))
                                    }
                                </tbody>
                            </table>
                        </Fragment>
                        : undefined
                }
                &nbsp;
                {
                    this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : ""
                }
                {
                    this.state.mode === "write"
                        ? <div className="action-panel">
                            <button className="button push-right" onClick={this.saveData} disabled={!this.state.hasChanged}>Save</button>
                            <button className="button" onClick={this.cancelData}>Cancel</button>
                        </div>
                        : undefined
                }
            </div>
        );
    }

    private async search() {
        try {
            if (!this.state.searchStart || !this.state.searchEnd) {
                this.setState({
                    employees: [],
                    isLoading: false,
                });
                return;
            }

            this.setState({
                isLoading: true
            });

            let after;
            let before;
            if (this.state.dateType === "month" && this.state.mode !== "write") {
                after = DateTime.fromISO(this.state.searchMonth).startOf("month");
                before = after.endOf("month");
            }
            else {
                after = DateTime.fromISO(this.state.searchStart).startOf("day");
                before = DateTime.fromISO(this.state.searchEnd).endOf("day");
            }

            const parameters = [
                `after=${after.toISO()}`,
                `before=${before.toISO()}`
            ];

            let data = [];
            switch (this.state.mode) {
                case "office":
                    data = await V2.get<any[]>(`metrics/provider/appointments?${parameters.join("&")}`);

                    const sumRowOffice = {
                        id: "total",
                        name: { first: "Total", last: "" },
                        hfAnnual: 0,
                        hfGyn: 0,
                        hfTotal: 0,
                        annual: 0,
                        gyn: 0,
                        total: 0
                    };

                    data.forEach(a => {
                        sumRowOffice.hfAnnual += a.hfAnnual || 0;
                        sumRowOffice.hfGyn += a.hfGyn || 0;
                        sumRowOffice.hfTotal += a.hfTotal || 0;
                        sumRowOffice.annual += a.annual || 0;
                        sumRowOffice.gyn += a.gyn || 0;
                        sumRowOffice.total += a.total || 0;
                    });

                    data.push(sumRowOffice);
                    break;
                case "surgery":
                    data = await V2.get<any[]>(`metrics/provider/surgeries?${parameters.join("&")}`);

                    const sumRow = {
                        id: "total",
                        name: { first: "Total", last: "" },
                        pending: 0,
                        cancel: 0,
                        done: 0,
                        total: 0
                    };

                    data.forEach(a => {
                        sumRow.pending += a.pending || 0;
                        sumRow.cancel += a.cancel || 0;
                        sumRow.done += a.done || 0;
                        sumRow.total += a.total || 0;
                    });

                    data.push(sumRow);
                    break;
                case "write":
                    data = await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`);

                    if (this.state.mode === "write") {
                        // Load in existing metric data for editing
                        const existingMetrics = await V2.get<any[]>(`metrics/${after.toISODate()}/provider/appointments`);
                        for (const item of existingMetrics) {
                            const employee = data.find(a => a.id === item.employeeId);
                            if (employee) {
                                employee.gyn = item.gyn;
                                employee.annual = item.annual;
                            }
                        }
                    }
                    break;
                default: break;
            }

            data.sort((a, b) => a.id === "total" ? 1 : formatName(a.name || a.employee.name).localeCompare(formatName(b.name || b.employee.name)));


            this.setState({
                employees: data,
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        let isWriteAllowed = this.state.isWriteAllowed;
        let searchStart = this.state.searchStart;
        if (name === "searchStart") {
            if (value) {
                const date = DateTime.fromISO(value).startOf("day");
                const today = DateTime.local().startOf("day");
                isWriteAllowed = (date.isValid && date.diff(today, "hours").hours <= 0);
            }
            else {
                isWriteAllowed = false;
            }
        }

        this.setState({
            // mode: "read",

            isWriteAllowed,
            searchStart,

            [name]: value
        }, () => this.search());
    }

    private handleRowInputChange(index: number, prop: string, e: ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;

        this.setState(s => {
            s.employees[index][prop] = value;

            return {
                employees: s.employees,
                hasChanged: true
            };
        })
    }

    private handleInputChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        });
    }

    private async saveData() {
        try {
            this.setState({
                isSubmitting: true
            });

            const data = {
                cases: this.state.employees
                    .filter(a => a.gyn || a.annual)
                    .map(a => ({
                        employeeId: a.id,

                        annual: +a.annual,
                        gyn: +a.gyn
                    }))
            };

            await V2.put(`metrics/${this.state.searchStart}/provider/appointments`, data);

            const today = DateTime.local();

            this.setState({
                isSubmitting: false,
                hasChanged: false,
                mode: "office",

                searchStart: today.startOf("month").toISODate(),
                searchEnd: today.endOf("month").toISODate(),
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e
            });
        }
    }

    private cancelData() {
        this.setState({
            mode: "office"
        }, () => this.search());
    }

    private toggleWriteMode() {
        const today = DateTime.local();

        this.setState({
            mode: "write",
            view: "office",

            searchStart: today.toISODate()
        }, () => this.search());
    }

    private handleTabChange(index: number) {
        let mode: ViewMode;
        switch (index) {
            case 0: mode = "office"; break;
            case 1: mode = "surgery"; break;
            default: mode = "office"; break;
        }

        this.setState({
            mode
        }, () => this.search());
    }
}

export default withTitle("Provider Stats", withAuth(MetricsNpCasesView));
